import { TimelinePlayerOptionsArea } from "./TimelinePlayerOptionsArea";
import { useIsPhone } from "./useIsPhone";
import { useCallback, useState, type RefObject } from "react";

import { shareAddress } from "@/lib/timeline/view/UrlOptions/address";
import { currentBookPath } from "./usePlayerAddressBar";

import type { TimelineSpan } from "@/data/join-chapters";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { ScrubDirection } from "@/lib/timeline/playback";
import type { FeatureFlags } from "@/config/features";
import type { JourneyMode } from "@/data/schema/compiled";
import type { TimelineUiPreferences } from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { TimelineKeyItems } from "./TimelineKeyItems";
import { TimelineMarkerNames } from "./TimelineMarkerNames";
import { TimelineAcquires } from "./TimelineAcquires";
import { TimelineDeaths } from "./TimelineDeaths";
import { TimelineVehicleLabels } from "./TimelineVehicleLabels";
import { TimelineSceneCaption } from "./TimelineSceneCaption";
import { TimelineTransport } from "./TimelineTransport";

interface BookTimelineStageChromeProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => import("../../lib/timeline/playback").PlaybackState;
  stageRef: RefObject<HTMLDivElement | null>;
  uiPrefs: TimelineUiPreferences;
  updateUiPrefs: (patch: Partial<TimelineUiPreferences>) => void;
  playing: boolean;
  speed: number;
  onPlayPause: () => void;
  onRestart: () => void;
  repeat: boolean;
  onRepeatToggle: () => void;
  onScrub: (direction: ScrubDirection) => void;
  onSkipScene: (direction: -1 | 1) => void;
  onSpeedChange: (speed: number) => void;
  onCameraLockToggle: () => void;
  onAutoZoomToggle: () => void;
  spans: TimelineSpan[];
  spanId: string;
  onSpanIdChange: (id: string) => void;
  featureFlags: FeatureFlags;
  journey: JourneyMode;
  onJourneyChange: (journey: JourneyMode) => void;
  onJourneyPrefetch: () => void;
}

export function BookTimelineStageChrome({
  canvas,
  layout,
  getPlayback,
  stageRef,
  uiPrefs,
  updateUiPrefs,
  playing,
  speed,
  onPlayPause,
  onRestart,
  repeat,
  onRepeatToggle,
  onScrub,
  onSkipScene,
  onSpeedChange,
  onCameraLockToggle,
  onAutoZoomToggle,
  spans,
  spanId,
  onSpanIdChange,
  featureFlags,
  journey,
  onJourneyChange,
  onJourneyPrefetch,
}: BookTimelineStageChromeProps) {
  const isPhone = useIsPhone();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetHost, setSheetHost] = useState<HTMLElement | null>(null);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const getShareLink = (): string => shareAddress(
    currentBookPath(),
    {
      journey,
      spanId,
      cameraLock: uiPrefs.cameraLockEnabled,
      autoZoom: uiPrefs.autoZoomEnabled,
    },
    getPlayback().playheadMs,
  );
  return (
    <>
      <TimelineSceneCaption
        canvas={canvas}
        layout={layout}
        getPlayback={getPlayback}
        spoilerLevel={uiPrefs.spoilerLevel}
        playbackDebugVisible={
          featureFlags.boardModelDebugMenu && uiPrefs.boardModelDebug
        }
      />
      <TimelineKeyItems
        canvas={canvas}
        layout={layout}
        getPlayback={getPlayback}
        spoilersOn={uiPrefs.spoilerLevel === "shown"}
      />
      <TimelineDeaths canvas={canvas} layout={layout} getPlayback={getPlayback} />
      <TimelineAcquires canvas={canvas} layout={layout} getPlayback={getPlayback} />
      <TimelineVehicleLabels canvas={canvas} layout={layout} getPlayback={getPlayback} />
      <TimelineMarkerNames
        canvas={canvas}
        layout={layout}
        getPlayback={getPlayback}
        spoilersOn={uiPrefs.spoilerLevel === "shown"}
      />
      <TimelinePlayerOptionsArea
        isPhone={isPhone}
        sheetOpen={sheetOpen}
        closeSheet={closeSheet}
        setSheetHost={setSheetHost}
        uiPrefs={uiPrefs}
        updateUiPrefs={updateUiPrefs}
        featureFlags={featureFlags}
        spans={spans}
        spanId={spanId}
        onSpanIdChange={onSpanIdChange}
        journey={journey}
        onJourneyChange={onJourneyChange}
        onJourneyPrefetch={onJourneyPrefetch}
        getShareLink={getShareLink}
      />
      <TimelineTransport
        stageRef={stageRef}
        transportSizeLevel={uiPrefs.transportSizeLevel}
        transportPosition={uiPrefs.transportPosition}
        onTransportPositionChange={(position) => {
          updateUiPrefs({ transportPosition: position });
        }}
        playing={playing}
        speed={speed}
        onPlayPause={onPlayPause}
        onRestart={onRestart}
        repeat={repeat}
        onRepeatToggle={onRepeatToggle}
        onScrub={onScrub}
        onSkipScene={onSkipScene}
        onSpeedChange={onSpeedChange}
        cameraLockEnabled={uiPrefs.cameraLockEnabled}
        autoZoomEnabled={uiPrefs.autoZoomEnabled}
        onCameraLockToggle={onCameraLockToggle}
        onAutoZoomToggle={onAutoZoomToggle}
        phoneSheet={isPhone ? { onOpen: () => setSheetOpen(true), host: sheetHost } : undefined}
      />
    </>
  );
}
